import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Printer } from "lucide-react";
import { cleanReportHtml } from "@/lib/sanitize";
import { fmtGbp, fmtGbp0, fmtPct0, variance } from "@/lib/constants";

const STATUS_LABEL = {
    not_started: "Not started", in_progress: "In progress", prepared: "Prepared",
    reviewed: "Reviewed", partner_signed: "Partner signed", na: "Not applicable",
};

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
}

export default function WorkingPaperPrint() {
    const { engagementId } = useParams();
    const [sp] = useSearchParams();
    const wpId = sp.get("wp");
    const [e, setE] = useState(null);
    const [wps, setWps] = useState(null);
    const [leads, setLeads] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [a, b] = await Promise.all([
                    api.get(`/engagements/${engagementId}`),
                    api.get(`/engagements/${engagementId}/working-papers`),
                ]);
                const papers = b.data;
                const leadPapers = papers.filter((w) => w.is_lead_schedule);
                const res = await Promise.all(leadPapers.map((w) => api.get(`/working-papers/${w.id}/lead-sheet`).then((r) => [w.id, r.data]).catch(() => [w.id, null])));
                const lmap = {}; res.forEach(([id, d]) => { if (d) lmap[id] = d; });
                setE(a.data); setWps(papers); setLeads(lmap);
            } catch { setLeads({}); }
        })();
    }, [engagementId]);

    useEffect(() => {
        if (e && wps && leads !== null) {
            let cancelled = false;
            const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
            ready.then(() => setTimeout(() => { if (!cancelled) window.print(); }, 500));
            return () => { cancelled = true; };
        }
    }, [e, wps, leads]);

    if (!e || !wps || leads === null) return <div style={{ padding: 40 }}>Preparing document…</div>;

    let papers = [...wps].sort((a, b) => (a.section || "").localeCompare(b.section || "") || (a.order - b.order) || (a.code || "").localeCompare(b.code || ""));
    if (wpId) papers = papers.filter((w) => w.id === wpId);
    const single = !!wpId;
    const period = e.period_start && e.period_end ? `${fmtDate(e.period_start)} to ${fmtDate(e.period_end)}` : "";

    return (
        <div className="wpp-root">
            <style>{CSS}</style>
            <button className="print-btn no-print" data-testid="wp-print-now" onClick={() => window.print()}><Printer size={15} /> Print / Save as PDF</button>

            <section className="page brk" data-testid="wp-print-cover">
                <div className="brand">QUBE ACCOUNTANTS</div>
                <h1>{e.client_name}</h1>
                {e.property_name ? <div className="prop">{e.property_name}</div> : null}
                <div className="doctitle">{single ? "Working Paper" : "Working Papers File"}</div>
                {period ? <div className="period">Period: {period}</div> : null}
                {e.managing_agent ? <div className="agent">Managing agent: {e.managing_agent}</div> : null}
                <div className="meta">Prepared {fmtDate(new Date().toISOString())} · {papers.length} paper{papers.length === 1 ? "" : "s"}</div>
            </section>

            {papers.map((w) => <PaperBlock key={w.id} w={w} lead={leads[w.id]} /> )}
        </div>
    );
}

function PaperBlock({ w, lead }) {
    const paperAtts = (w.attachments || []).filter((a) => !a.step_id);
    return (
        <section className="page brk paper" data-testid={`wp-print-${w.code}`}>
            <div className="wp-head">
                <div className="wp-ref">{w.section} · {w.code}</div>
                <div className="wp-badges">
                    {w.is_lead_schedule && <span className="badge lead">Lead schedule</span>}
                    {w.risk && w.risk !== "none" && <span className={`badge risk-${w.risk}`}>{w.risk} risk</span>}
                    <span className="badge status">{STATUS_LABEL[w.status] || w.status}</span>
                </div>
            </div>
            <h2 className="wp-title">{w.title}</h2>
            {w.purpose ? <p className="wp-purpose">{w.purpose}</p> : null}

            {(w.steps || []).length > 0 && (
                <ol className="steps">
                    {w.steps.map((s, i) => <StepBlock key={s.id || i} s={s} n={s.label || String(i + 1)} atts={(w.attachments || []).filter((a) => a.step_id === s.id)} />)}
                </ol>
            )}

            {w.is_lead_schedule && (
                lead && lead.rows && lead.rows.length
                    ? <LeadTable lead={lead} code={w.code} />
                    : <p className="note-line">Lead schedule figures reconcile to the trial balance; the numeric statement is in the Accounts pack.</p>
            )}

            {(w.findings || "").trim() && (
                <div className="findings">
                    <div className="findings-h">Conclusion / findings</div>
                    <div className="rte" dangerouslySetInnerHTML={{ __html: cleanReportHtml(w.findings) }} />
                </div>
            )}

            {paperAtts.length > 0 && (
                <div className="atts">
                    <div className="atts-h">Attachments</div>
                    <ul>{paperAtts.map((a) => <li key={a.id}><a href={a.url} target="_blank" rel="noreferrer">{a.label || a.url}</a></li>)}</ul>
                </div>
            )}

            <div className="signoffs">
                <SignOff label="Prepared by" so={w.prepared_by} />
                <SignOff label="Reviewed by" so={w.reviewed_by} />
                <SignOff label="Partner signed" so={w.partner_signed_by} />
            </div>
        </section>
    );
}

function LeadTable({ lead, code }) {
    const rows = lead.rows || [];
    const fin = (r) => Number(r.final != null ? r.final : r.current_period) || 0;
    const pre = (r) => Number(r.prelim != null ? r.prelim : r.current_period) || 0;
    const sum = (f) => rows.reduce((s, r) => s + f(r), 0);
    const isAR = code === "B2" || code === "F1";
    const isB2 = code === "B2";
    if (isAR) {
        // Condensed AR: single value column (prelim for B2, final for F1), whole-number rounding, with variances.
        const val = (r) => isB2 ? pre(r) : fin(r);
        const showBudget = rows.some((r) => Number(r.budget));
        const span = 4 + (showBudget ? 2 : 0);
        const tv = sum(val), tpr = sum((r) => Number(r.prior_period) || 0), tb = sum((r) => Number(r.budget) || 0);
        return (
            <table className="lead-tbl" data-testid="wp-print-lead-table">
                <thead><tr><th>Code</th><th>Account</th><th className="r">{isB2 ? "Prelim" : "Final"}</th><th className="r">Prior</th>{showBudget && <th className="r">Budget</th>}<th className="r">Var vs prior</th>{showBudget && <th className="r">Var vs budget</th>}</tr></thead>
                <tbody>
                    {rows.map((r) => {
                        const cmt = r.effective_comment != null ? r.effective_comment : (r.line_comment || "");
                        return (
                            <React.Fragment key={r.id}>
                                <tr>
                                    <td className="code">{r.account_code}</td>
                                    <td>{r.account_name}</td>
                                    <td className="r b">{fmtGbp0(val(r))}</td>
                                    <td className="r">{fmtGbp0(r.prior_period)}</td>
                                    {showBudget && <td className="r">{fmtGbp0(r.budget)}</td>}
                                    <td className="r">{fmtPct0(variance(val(r), r.prior_period))}</td>
                                    {showBudget && <td className="r">{fmtPct0(variance(val(r), r.budget))}</td>}
                                </tr>
                                {cmt ? <tr className="cmt"><td></td><td colSpan={span} className="cmt-td">{cmt}</td></tr> : null}
                            </React.Fragment>
                        );
                    })}
                    <tr className="tot"><td colSpan={2}>Total</td><td className="r b">{fmtGbp0(tv)}</td><td className="r">{fmtGbp0(tpr)}</td>{showBudget && <td className="r">{fmtGbp0(tb)}</td>}<td className="r">{fmtPct0(variance(tv, tpr))}</td>{showBudget && <td className="r">{fmtPct0(variance(tv, tb))}</td>}</tr>
                </tbody>
            </table>
        );
    }
    const tp = sum(pre), ta = sum((r) => Number(r.adjustment) || 0), tf = sum(fin), tpr = sum((r) => Number(r.prior_period) || 0);
    return (
        <table className="lead-tbl" data-testid="wp-print-lead-table">
            <thead><tr><th>Code</th><th>Account</th><th className="r">Prelim</th><th className="r">Adjustments</th><th className="r">Final</th><th className="r">Prior</th></tr></thead>
            <tbody>
                {rows.map((r) => {
                    const cmt = r.effective_comment != null ? r.effective_comment : (r.line_comment || "");
                    return (
                        <React.Fragment key={r.id}>
                            <tr>
                                <td className="code">{r.account_code}</td>
                                <td>{r.account_name}</td>
                                <td className="r">{fmtGbp(pre(r))}</td>
                                <td className="r">{r.adjustment ? fmtGbp(r.adjustment) : "—"}</td>
                                <td className="r b">{fmtGbp(fin(r))}</td>
                                <td className="r">{fmtGbp(r.prior_period)}</td>
                            </tr>
                            {cmt ? <tr className="cmt"><td></td><td colSpan={5} className="cmt-td">{cmt}</td></tr> : null}
                        </React.Fragment>
                    );
                })}
                <tr className="tot"><td colSpan={2}>Total</td><td className="r">{fmtGbp(tp)}</td><td className="r">{ta ? fmtGbp(ta) : "—"}</td><td className="r b">{fmtGbp(tf)}</td><td className="r">{fmtGbp(tpr)}</td></tr>
            </tbody>
        </table>
    );
}

function StepBlock({ s, n, atts }) {
    const answers = Object.entries(s.answers || {}).filter(([, v]) => String(v || "").trim());
    const inputLabel = (k) => (s.inputs || []).find((x) => x.key === k) || {};
    const values = Object.entries(s.values || {}).filter(([, v]) => String(v ?? "").trim());
    return (
        <li className="step">
            <div className="step-t"><span className="step-n">{n}</span> {s.title}{s.done ? <span className="tick">✓ done{s.done_at ? ` · ${fmtDate(s.done_at)}` : ""}</span> : null}</div>
            {s.guidance ? <div className="step-g">{s.guidance}</div> : null}
            {s.outcome ? <div className="kv"><b>Outcome:</b> {s.outcome}</div> : null}
            {answers.map(([k, v]) => <div className="kv" key={k}><b>{k}:</b> {v}</div>)}
            {values.map(([k, v]) => <div className="kv" key={k}><b>{inputLabel(k).label || k}:</b> {String(v)}{inputLabel(k).unit ? ` ${inputLabel(k).unit}` : ""}</div>)}
            {s.used_standard ? <div className="kv"><b>Standard procedure applied.</b></div> : null}
            {(s.comment || "").trim() ? <div className="step-c">{s.comment}</div> : null}
            {(atts || []).length > 0 && (
                <ul className="step-atts">{atts.map((a) => <li key={a.id}><a href={a.url} target="_blank" rel="noreferrer">{a.label || a.url}</a></li>)}</ul>
            )}
        </li>
    );
}

function SignOff({ label, so }) {
    return (
        <div className="so">
            <div className="so-l">{label}</div>
            <div className="so-v">{so && so.user_name ? <>{so.user_name}<span className="so-d">{so.at ? fmtDate(so.at) : ""}</span></> : <span className="so-empty">—</span>}</div>
        </div>
    );
}

const CSS = `
.wpp-root{background:#fff;color:#111;font-family:'IBM Plex Sans',ui-sans-serif,system-ui,sans-serif}
.print-btn{position:fixed;top:16px;right:16px;z-index:1000;background:#1e5a85;color:#fff;border:none;border-radius:999px;padding:10px 16px;font-size:13px;display:flex;gap:8px;align-items:center;cursor:pointer;box-shadow:0 8px 20px -6px rgba(30,90,133,.5)}
.page{max-width:820px;margin:0 auto;padding:40px 52px;position:relative}
.page + .page{border-top:1px dashed #ddd}
[data-testid="wp-print-cover"]{text-align:center;padding-top:60px}
.brand{font-family:'Chivo',sans-serif;letter-spacing:5px;color:#1e5a85;font-weight:700;font-size:13px;margin-bottom:32px}
[data-testid="wp-print-cover"] h1{font-family:'Chivo',sans-serif;font-weight:800;font-size:32px;margin:0 0 6px}
.prop{color:#555;font-size:16px;margin-bottom:18px}
.doctitle{font-family:'Chivo',sans-serif;font-size:22px;color:#1e5a85;margin:14px 0 8px}
.period,.agent{font-size:14px;color:#333}
.meta{margin-top:36px;color:#777;font-size:12px}
.paper .wp-head{display:flex;align-items:center;gap:10px;margin-bottom:4px}
.wp-ref{font-family:'IBM Plex Mono',monospace;font-weight:700;font-size:12px;background:#f1f5f9;border-radius:4px;padding:3px 8px}
.wp-badges{display:flex;gap:6px;margin-left:auto;flex-wrap:wrap}
.badge{font-size:10px;text-transform:uppercase;letter-spacing:.08em;font-weight:700;border-radius:4px;padding:3px 7px}
.badge.lead{background:#e6f0f6;color:#1e5a85}
.badge.status{background:#f1f5f9;color:#556}
.badge.risk-high{background:#fde8e8;color:#b42318}
.badge.risk-medium{background:#fff4e5;color:#b25e09}
.badge.risk-low{background:#e7f6ec;color:#15803d}
.wp-title{font-family:'Chivo',sans-serif;font-weight:700;font-size:20px;color:#111;margin:2px 0 8px}
.wp-purpose{font-size:13px;color:#555;margin:0 0 14px}
.note-line{font-size:12px;color:#777;font-style:italic;margin:10px 0}
.lead-tbl{width:100%;border-collapse:collapse;margin:10px 0 4px;font-size:12px}
.lead-tbl th{text-align:left;font-family:'Chivo',sans-serif;font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#667;border-bottom:1.5px solid #ccd;padding:5px 8px}
.lead-tbl td{padding:5px 8px;border-bottom:1px solid #eef}
.lead-tbl td.code{font-family:'IBM Plex Mono',monospace;font-size:11px;color:#778}
.lead-tbl .r{text-align:right;font-family:'IBM Plex Mono',monospace}
.lead-tbl .b{font-weight:700}
.lead-tbl tr.cmt td.cmt-td{font-size:11px;color:#667;font-style:italic;padding-top:0;border-bottom:1px solid #eef}
.lead-tbl tr.tot td{border-top:2px solid #ccd;border-bottom:none;font-weight:700;background:#f7f9fb}
.steps{list-style:none;padding:0;margin:0}
.step{border-top:1px solid #eee;padding:10px 0}
.step-t{font-size:14px;font-weight:600}
.step-n{font-family:'IBM Plex Mono',monospace;color:#1e5a85;font-weight:700;margin-right:6px}
.tick{color:#15803d;font-size:11px;font-weight:600;margin-left:8px}
.step-g{font-size:12px;color:#667;margin:3px 0;white-space:pre-wrap}
.kv{font-size:13px;margin:3px 0}
.kv b{color:#333}
.step-c{font-size:13px;background:#f8fafc;border-left:3px solid #cbd5e1;padding:6px 10px;margin:6px 0;white-space:pre-wrap}
.step-atts,.atts ul{list-style:disc;margin:6px 0 0 18px;padding:0;font-size:12px}
.step-atts a,.atts a{color:#1e5a85;text-decoration:underline}
.findings{margin-top:14px;border:1px solid #e2e8f0;border-radius:6px;padding:10px 12px}
.findings-h,.atts-h{font-family:'Chivo',sans-serif;font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#1e5a85;font-weight:700;margin-bottom:5px}
.rte{font-size:13px;line-height:1.6}
.rte table{border-collapse:collapse;width:100%;margin:8px 0}
.rte th,.rte td{border:1px solid #ccc;padding:4px 7px;font-size:12px}
.atts{margin-top:14px}
.signoffs{display:flex;gap:18px;margin-top:20px;border-top:2px solid #333;padding-top:10px}
.so{flex:1}
.so-l{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#889;font-weight:700}
.so-v{font-size:13px;margin-top:3px}
.so-d{display:block;font-size:11px;color:#889;font-family:'IBM Plex Mono',monospace}
.so-empty{color:#bbb}
@media print{
  .no-print{display:none !important}
  .page{padding:6mm 4mm;max-width:100%}
  .page + .page{border-top:none}
  .brk{break-after:page;page-break-after:always}
  .step{break-inside:avoid;page-break-inside:avoid}
  .findings,.signoffs{break-inside:avoid;page-break-inside:avoid}
  @page{margin:12mm}
}`;
